import React from 'react';
import {
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowRight,
  IconBuildingFactory2,
  IconChartBar,
  IconChecklist,
  IconClipboardText,
  IconFileInvoice,
  IconPackage,
  IconReceipt2,
  IconRotate,
  IconShieldCheck,
  IconShoppingCart,
  IconTruckDelivery,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

function StatCard({ title, value, subtitle, color, icon: Icon }) {
  return (
    <Card withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text fw={700} size="1.5rem" lh={1.1}>
            {value}
          </Text>
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        </Stack>

        <ThemeIcon color={color} variant="light" size={48} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function ModuleCard({ title, description, color, icon: Icon, onClick, badge }) {
  return (
    <Card
      withBorder
      radius="lg"
      p="lg"
      shadow="sm"
      style={{ cursor: 'pointer', transition: 'transform 0.18s ease, box-shadow 0.18s ease' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
      onClick={onClick}
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <ThemeIcon color={color} variant="light" size={54} radius="md">
            <Icon size={28} />
          </ThemeIcon>
          {badge ? (
            <Badge variant="light" color={color}>
              {badge}
            </Badge>
          ) : null}
        </Group>

        <div>
          <Text fw={700} size="lg">
            {title}
          </Text>
          <Text size="sm" c="dimmed" mt="sm" style={{ minHeight: 44 }}>
            {description}
          </Text>
        </div>

        <Button variant="light" color={color} rightSection={<IconArrowRight size={16} />}>
          Modüle Git
        </Button>
      </Stack>
    </Card>
  );
}

function QuickFlowCard({ step, title, description, color, icon: Icon }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Group align="flex-start" wrap="nowrap">
        <ThemeIcon color={color} variant="light" radius="md" size={42}>
          <Icon size={20} />
        </ThemeIcon>
        <div style={{ flex: 1 }}>
          <Group gap="xs" mb={4}>
            <Badge variant="light" color={color}>
              Adım {step}
            </Badge>
            <Text fw={700} size="sm">
              {title}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        </div>
      </Group>
    </Paper>
  );
}

export default function ProcurementHomePage() {
  const navigate = useNavigate();

  const moduleCards = [
    {
      title: 'Talep Oluşturma',
      description: 'Yeni satın alma ihtiyaçlarını başlık, gerekçe ve satır bazlı kalemlerle sisteme girin.',
      color: 'blue',
      icon: IconClipboardText,
      path: '/request',
      badge: 'Başlangıç',
    },
    {
      title: 'Talep Onayı',
      description: 'Açılan talepleri onay akışına göre inceleyin, karar verin ve süreci ilerletin.',
      color: 'orange',
      icon: IconShieldCheck,
      path: '/approval',
      badge: 'Onay',
    },
    {
      title: 'Teklif Toplama',
      description: 'Onaylanan talepler için tedarikçi tekliflerini başlık ve satır bazında kaydedin.',
      color: 'teal',
      icon: IconReceipt2,
      path: '/quote-entry',
      badge: 'Teklif',
    },
    {
      title: 'Teklif Seçimi',
      description: 'Toplanan teklifleri karşılaştırın, gerekçe ile en uygun teklifi seçin.',
      color: 'violet',
      icon: IconChecklist,
      path: '/quote-selection',
      badge: 'Karar',
    },
    {
      title: 'Mal Kabul',
      description: 'Teslim alınan ürünlerde kabul, red, eksik teslim ve hasar kontrolünü yönetin.',
      color: 'cyan',
      icon: IconPackage,
      path: '/goods-receipt',
      badge: 'Operasyon',
    },
    {
      title: 'İade Yönetimi',
      description: 'Sorunlu kalemler için iade, düzeltme ve tedarikçiye geri gönderim sürecini takip edin.',
      color: 'red',
      icon: IconRotate,
      path: '/return-management',
      badge: 'İade',
    },
    {
      title: 'Fatura Kontrol',
      description: 'Mal kabul sonrası faturaları kaydedin, PDF yükleyin ve finansal mutabakatı kontrol edin.',
      color: 'grape',
      icon: IconFileInvoice,
      path: '/invoice-management',
      badge: 'Finans',
    },
    {
      title: 'Tedarikçi Ağı',
      description: 'Tedarikçi performansı, iletişim bilgileri ve tedarikçi havuzunu tek ekranda yönetin.',
      color: 'lime',
      icon: IconBuildingFactory2,
      path: '/network-page',
      badge: 'Tedarikçi',
    },
    {
      title: 'Analitik Dashboard',
      description: 'Harcama, termin, iade, tedarikçi performansı ve bütçe görünümünü yönetsel olarak izleyin.',
      color: 'indigo',
      icon: IconChartBar,
      path: '/purchase-reports',
      badge: 'Rapor',
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper
          withBorder
          radius="xl"
          p="xl"
          shadow="sm"
          style={{
            background: 'linear-gradient(135deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
          }}
        >
          <Group justify="space-between" align="flex-start" gap="xl">
            <div>
              <Badge variant="light" color="blue" leftSection={<IconShoppingCart size={14} />}>
                Satın Alma Modülü
              </Badge>
              <Title order={1} mt="md">
                Satın Alma Yönetim Merkezi
              </Title>
              <Text size="sm" c="dimmed" mt="sm" maw={920}>
                Satın alma süreçlerinin tamamını tek merkezden yönetin. Talep oluşturma, onay, teklif toplama,
                teklif seçimi, mal kabul, iade, fatura kontrolü, tedarikçi yönetimi ve analitik ekranlara buradan erişebilirsiniz.
              </Text>
            </div>

            <Button color="blue" leftSection={<IconArrowRight size={16} />} onClick={() => navigate('/request')}>
              Yeni Talep Başlat
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} spacing="md" mt="xl">
            <StatCard
              title="Aktif Süreç"
              value="9 Modül"
              subtitle="Uçtan uca satın alma akışı"
              color="blue"
              icon={IconChecklist}
            />
            <StatCard
              title="Operasyon Alanı"
              value="Talep → Fatura"
              subtitle="Tam süreç kapsaması"
              color="teal"
              icon={IconTruckDelivery}
            />
            <StatCard
              title="Kontrol Katmanı"
              value="Onay + Mutabakat"
              subtitle="Karar ve finansal doğrulama"
              color="orange"
              icon={IconShieldCheck}
            />
            <StatCard
              title="Yönetim Görünümü"
              value="Analitik + Tedarikçi"
              subtitle="Raporlama ve performans takibi"
              color="indigo"
              icon={IconChartBar}
            />
          </SimpleGrid>
        </Paper>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, xl: 8.5 }}>
            <Stack gap="lg">
              <SectionPaperTitle
                title="Modüller ve Yönlendirmeler"
                description="Satın alma modülü altındaki ekranlara hızlı erişim sağlayın."
              />

              <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="lg">
                {moduleCards.map((card) => (
                  <ModuleCard
                    key={card.title}
                    title={card.title}
                    description={card.description}
                    color={card.color}
                    icon={card.icon}
                    badge={card.badge}
                    onClick={() => navigate(card.path)}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xl: 3.5 }}>
            <Stack gap="lg">
              <Paper withBorder radius="lg" p="lg" shadow="sm">
                <Title order={4}>Süreç Akışı</Title>
                <Text size="sm" c="dimmed" mt={4} mb="md">
                  Satın alma yaşam döngüsünün kısa akış özeti.
                </Text>

                <Stack gap="sm">
                  <QuickFlowCard
                    step="1"
                    title="Talep Açılışı"
                    description="İhtiyaç tanımlanır ve kalemler oluşturulur."
                    color="blue"
                    icon={IconClipboardText}
                  />
                  <QuickFlowCard
                    step="2"
                    title="Onay ve Teklif"
                    description="Talep onaylanır, tedarikçi teklifleri toplanır ve seçilir."
                    color="orange"
                    icon={IconShieldCheck}
                  />
                  <QuickFlowCard
                    step="3"
                    title="Teslimat ve Finans"
                    description="Mal kabul, iade ve fatura işlemleri yürütülür."
                    color="teal"
                    icon={IconFileInvoice}
                  />
                </Stack>
              </Paper>

              <Paper withBorder radius="lg" p="lg" shadow="sm">
                <Title order={4}>Hızlı Erişim</Title>
                <Stack gap="sm" mt="md">
                  <Button variant="light" color="blue" fullWidth onClick={() => navigate('/approval')}>
                    Onay Bekleyen Talepler
                  </Button>
                  <Button variant="light" color="teal" fullWidth onClick={() => navigate('/quote-selection')}>
                    Teklif Seçim Ekranı
                  </Button>
                  <Button variant="light" color="cyan" fullWidth onClick={() => navigate('/goods-receipt')}>
                    Mal Kabul İşlemleri
                  </Button>
                  <Button variant="light" color="grape" fullWidth onClick={() => navigate('/purchase-reports')}>
                    Analitik Dashboard
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}

function SectionPaperTitle({ title, description }) {
  return (
    <Paper withBorder radius="lg" p="lg" shadow="sm">
      <Title order={4}>{title}</Title>
      <Text size="sm" c="dimmed" mt={4}>
        {description}
      </Text>
    </Paper>
  );
}
